import { canEditStep } from './shared-responder-v2-core-edit.helpers';
import {
  applyDraftEdit,
  isReplyingToFollowUp,
} from './shared-responder-v2-core-follow-up-reply.helpers';
import { selectCanReturnToIntro } from './shared-responder-v2-core-host-header.selectors';
import { applyStepLabels } from './shared-responder-v2-core-label.helpers';
import { applySessionResume } from './shared-responder-v2-core-resume.helpers';
import {
  acceptThreadMessage,
  commitCurrentDraft,
  failThread,
  submitAnswer,
  submitTurnEdit,
} from './shared-responder-v2-core-session-commit.helpers';
import {
  advance,
  exitEdit,
  isStaleResult,
  jumpTo,
  replaceCompletionBlock,
  withThread,
} from './shared-responder-v2-core-session-machine.helpers';
import { markFollowUpSkipped } from './shared-responder-v2-core-session-turns.helpers';
import {
  DEVICE_GATE_VERDICTS,
  RESPONDER_PHASES,
  type DeviceGateVerdict,
  type ResponderPhase,
} from './shared-responder-v2-core-session.consts';
import type { ResponderEvent } from './shared-responder-v2-core-session.events';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import {
  SITE_VISITS,
  THREAD_STATUSES,
  TURN_ROLES,
} from './shared-responder-v2-core-thread.consts';

function resolvePostGatePhase(
  state: ResponderSessionState,
  verdict: DeviceGateVerdict
): ResponderPhase {
  if (verdict !== DEVICE_GATE_VERDICTS.Allowed) {
    return RESPONDER_PHASES.DeviceGate;
  }

  return state.capabilities.hasIntro
    ? RESPONDER_PHASES.Intro
    : RESPONDER_PHASES.Question;
}

export function responderSessionReducer(
  state: ResponderSessionState,
  event: ResponderEvent
): ResponderSessionState {
  switch (event.type) {
    case 'deviceGateResolved':
      return {
        ...state,
        deviceGate: event.verdict,
        phase: resolvePostGatePhase(state, event.verdict),
      };

    case 'introAcknowledged':
      return { ...state, phase: RESPONDER_PHASES.Question };

    case 'introReturnRequested':
      return selectCanReturnToIntro(state)
        ? { ...state, phase: RESPONDER_PHASES.Intro }
        : state;

    case 'modeChanged':
      return state.capabilities.allowVoice
        ? { ...state, mode: event.mode }
        : state;

    case 'draftEdited':
      return applyDraftEdit(state, event.questionId, event.event);

    case 'answerSubmitted':
      return submitAnswer(state, event.questionId);

    case 'turnEditSubmitted':
      return submitTurnEdit(state, event);

    case 'continuePressed':
      return advance(commitCurrentDraft(state));

    case 'jumpRequested':
      return jumpTo(state, event.stepIndex);

    case 'answerEditRequested': {
      const step = state.steps[event.stepIndex];

      return step !== undefined && canEditStep(state, event.stepIndex)
        ? {
            ...state,
            editing: {
              mode: 'editing',
              questionId: step.questionId,
              returnStepIndex: state.currentStepIndex,
              draftBeforeEdit: state.threads[step.questionId]?.draft ?? null,
            },
            currentStepIndex: event.stepIndex,
          }
        : state;
    }

    case 'sessionResumed':
      return applySessionResume(state, {
        stepIndex: event.stepIndex,
        isComplete: event.isComplete,
      });

    case 'editCancelled':
      return exitEdit(state);

    case 'distributionToggled':
      return withThread(state, event.questionId, (thread) => ({
        ...thread,
        isDistributionVisible: !thread.isDistributionVisible,
      }));

    case 'completionBlockReplaced':
      return replaceCompletionBlock(state, event.questionIds);

    case 'sessionErrored':
      return { ...state, phase: RESPONDER_PHASES.SessionError };

    case 'labelsResolved':
      return applyStepLabels(state, event.labels);

    case 'threadInitiated':
      return isStaleResult(state, event.questionId, event.seq)
        ? state
        : withThread(state, event.questionId, (thread) =>
            // Once only. Two readers race for the same history on a resumed
            // session — the current question's own open and the progress walk —
            // and a second hydration would prepend every turn twice.
            thread.threadId !== null
              ? thread
              : {
                  ...thread,
                  threadId: event.threadId,
                  status: event.isComplete
                    ? THREAD_STATUSES.Closed
                    : THREAD_STATUSES.Open,
                  // History first: the resume read can resolve after the
                  // responder has already answered, and appending it then put
                  // the resumed conversation *below* the turn just typed.
                  turns: [...event.priorTurns, ...thread.turns],
                  // The first responder message is the question's own answer.
                  answerMessageId:
                    thread.answerMessageId ??
                    event.priorTurns.find(
                      (turn) => turn.role === TURN_ROLES.Responder
                    )?.id ??
                    null,
                }
          );

    case 'threadMessageAccepted':
      return isStaleResult(state, event.questionId, event.seq)
        ? state
        : acceptThreadMessage(
            state,
            event.questionId,
            event.result,
            event.liveResults ?? null
          );

    case 'followUpSkipped':
      return isStaleResult(state, event.questionId, event.seq) ||
        !isReplyingToFollowUp(state, event.questionId)
        ? state
        : withThread(state, event.questionId, markFollowUpSkipped);

    case 'threadFailed':
      return isStaleResult(state, event.questionId, event.seq)
        ? state
        : failThread(state, event.questionId, event.message);

    case 'siteVisitRecorded': {
      const nextState = withThread(state, event.questionId, (thread) =>
        thread.siteVisit === SITE_VISITS.Opened
          ? thread
          : { ...thread, siteVisit: event.visit }
      );

      return nextState !== state &&
        event.visit === SITE_VISITS.Opened &&
        event.url !== null
        ? { ...nextState, lastOpenedSiteUrl: event.url }
        : nextState;
    }

    case 'turnRetryRequested':
      return withThread(state, event.questionId, (thread) =>
        thread.status === THREAD_STATUSES.Failed && thread.answer !== null
          ? {
              ...thread,
              status: THREAD_STATUSES.AwaitingAssistant,
              error: null,
              requestSeq: thread.requestSeq + 1,
            }
          : thread
      );
    default: {
      const unexpected: never = event;
      throw new Error('Unexpected session event: ' + String(unexpected));
    }
  }
}
